import { useCallback, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { clsx } from "clsx";
import { $getSelection, $isRangeSelection, TextNode, CLEAR_EDITOR_COMMAND } from "lexical";
import uniqBy from "lodash/uniqBy";
import { createPortal } from "react-dom";

import { $createCustomVariableNode, $isCustomVariableNode } from "../../nodes/CustomVariableNode";
import { LexicalFilteredMenuPlugin } from "../../vendor/LexicalFilteredMenuPlugin";
import { ConfigurableVariableMenuItem } from "./Components/ConfigurableVariableMenuItem/ConfigurableVariableMenuItem";
import { ConfigurableVariableOption } from "./ConfigurableVariableOption";
import { useTypeaheadFilterMatch } from "./Hooks/useTypeaheadFilterMatch/useTypeheadFilterMatch";

import styles from "./ConfigurableVariablesPickerPlugin.module.scss";

type TypeaheadOptionsPickerPluginProps = {
    popAbove?: boolean;
    fixedWidth?: boolean;
    loading: boolean;
    allOptions: Array<ConfigurableVariableOption>;
    denylist: Array<string>;
};

/**
 * @note this plugin is used to provide a typeahead picker for the lexical editor
 * that we can trigger on arbitrary strings without a specific trigger
 * --
 * it will also clear the options when we select to get around an issue
 * where clicking in the middle of middle of a trigger was
 * keeping the right had side in the form on select
 */
export function TypeaheadOptionsPickerPlugin({
    popAbove = false,
    fixedWidth = false,
    loading,
    allOptions,
    denylist,
}: TypeaheadOptionsPickerPluginProps) {
    const [editor] = useLexicalComposerContext();

    const [mouseX, setMouseX] = useState(-1);
    const [mouseY, setMouseY] = useState(-1);

    const { triggerCallbacks } = useTypeaheadFilterMatch({
        options: allOptions,
        denylist,
    });
    const onSelectOption = useCallback(
        (
            selectedOption: ConfigurableVariableOption,
            nodeToRemove: TextNode | null,
            closeMenu: () => void,
            matchedString: string
        ) => {
            // clear any text that was in the editor and replace it with the selected option
            editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);

            editor.update(() => {
                const selection = $getSelection();

                if (!$isRangeSelection(selection) || selectedOption == null) {
                    return;
                }

                if (nodeToRemove) {
                    // this represents the text node that triggered the custom variable picker menu
                    // we want to remove that partial text (used as a query to search for custom variables)
                    // and replace it with the selected custom variable node
                    nodeToRemove.remove();
                } else {
                    const [currentNode] = selection.getNodes();
                    if ($isCustomVariableNode(currentNode) && matchedString.length) {
                        // we want to remove the node under the cursor
                        // since we're going to insert the selected CustomVariableNode
                        // at the same cursor position
                        currentNode.remove();
                    }
                }

                selection.insertNodes([$createCustomVariableNode(`${selectedOption.text}`)]);
                // TODO:  FIX THIS HACK
                setTimeout(() => {
                    closeMenu();
                }, 0);
            });
        },
        [editor]
    );
    const handleQueryChange = useCallback(
        (query: string | null) => {
            let filteredOptions = allOptions;
            const safeQuery = query?.trim()?.toLowerCase();
            if (safeQuery) {
                filteredOptions = filteredOptions.filter((option) => option?.value?.toLowerCase()?.includes(safeQuery));
            }

            const newQueryOPtions = uniqBy(filteredOptions, "value");
            return newQueryOPtions;
        },
        [allOptions]
    );

    return (
        <LexicalFilteredMenuPlugin
            onQueryChange={handleQueryChange}
            onSelectOption={onSelectOption}
            triggerFns={triggerCallbacks}
            allOptions={allOptions}
            fixedMenu={popAbove} // if forced pop above, fix menu to not reposition
            menuRenderFn={(
                anchorElementRef,
                { selectedIndex, options, selectOptionAndCleanUp, setHighlightedIndex }
            ) => {
                if (anchorElementRef.current == null) {
                    return null;
                }

                if (loading) {
                    return createPortal(
                        <div
                            className={clsx("typeahead-popover custom-variables-menu", {
                                "typeahead-popover-popabove": popAbove,
                            })}
                        >
                            <div className={styles.loading}>Loading...</div>
                        </div>,
                        anchorElementRef.current
                    );
                }

                return anchorElementRef.current && options.length
                    ? createPortal(
                          <div
                              className={clsx(styles.menuContainer, "typeahead-popover custom-variables-menu", {
                                  "typeahead-popover-popabove": popAbove,
                                  "typeahead-popover-fixedsize": fixedWidth,
                              })}
                          >
                              <ul>
                                  {options.map((option: ConfigurableVariableOption, index) => (
                                      <ConfigurableVariableMenuItem
                                          key={`${index} ${option.value}`}
                                          isSelected={selectedIndex === index}
                                          onClick={() => {
                                              setHighlightedIndex(index);
                                              selectOptionAndCleanUp(option);
                                          }}
                                          onMouseEnter={(event) => {
                                              if (event.pageX !== mouseX || event.pageY !== mouseY) {
                                                  setMouseX(event.pageX);
                                                  setMouseY(event.pageY);
                                                  setHighlightedIndex(index);
                                              }
                                          }}
                                          onMouseMove={() => {
                                              if (index !== selectedIndex) {
                                                  setHighlightedIndex(index);
                                              }
                                          }}
                                          option={option}
                                      />
                                  ))}
                              </ul>
                          </div>,
                          anchorElementRef.current
                      )
                    : null;
            }}
        />
    );
}
